import React, { useCallback, useState } from "react";
import LayoutV1 from "markup/Layout/LayoutV1";
import { isValidTurkishPhoneNumber } from "helpers/PhoneFormats";
import { toast } from "react-toastify";
import {
  makeStyles,
  MenuItem,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
} from "@material-ui/core";
import PhoneField from "modules/Auth/parts/PhoneField";
import { connect, useSelector } from "react-redux";
import * as advertRedux from "modules/Advert/_redux/advertRedux";
import * as categoryRedux from "modules/Category/_redux/categoryRedux";
import CreateAdvertFilters from "modules/Advert/views/parts/CreateAdvertFilters";
import Button from "@material-ui/core/Button";
import { formatNumberWithCommas } from "helpers/FormatNumbersWithComma";
import ProvinceDistrictSelect from "modules/Advert/views/parts/locations/ProvinceDistrictSelect";
import * as advertEndpoint from "modules/Advert/_redux/advertEndpoint";
import { useDropzone } from "react-dropzone";
import FastAdvertSuccess from "markup/Element/FastAdvertSuccess";

// Mirrors FastAdvertCreationPage but for kind=seller. Differences:
//   - single `price` instead of priceMin/priceMax
//   - required `condition` (sıfır / 2.el / yenilenmiş)
//   - up to 8 photos (vs single optional photo for buyer)
//   - takas (tradeable) toggle, contact preference

const formDataKey = "FAST_SELLER_FORM";

function getSteps() {
  return [
    "Kategori",
    "Fotoğraflar",
    "Başlık & Açıklama",
    "Fiyat & Durum",
    "Konum",
    "İletişim",
    "Yayınla",
  ];
}

function FastSellerAdvertCreationPage(props) {
  const classes = useStyles();
  const steps = getSteps();

  const [user, setUser] = useState({ phoneNumber: "", fullname: "", email: "" });
  const [activeStep, setActiveStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const { categories, newAdvert } = useSelector((state) => ({
    categories: state.category.categories,
    newAdvert: state.advert.newAdvert,
  }));

  // Ensure kind is set to 'seller' on mount.
  React.useEffect(() => {
    if (newAdvert.kind !== "seller") props.editNewAdvert("kind", "seller");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => {
      const merged = [...prev, ...acceptedFiles].slice(0, 8);
      return merged;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: true,
  });

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);
  const handleReset = () => setActiveStep(0);

  const selectCategory = (category) => {
    props.selectCategory(category);
    handleNext();
  };

  const handleUserFormData = (name, value) =>
    setUser((prev) => ({ ...prev, [name]: value }));

  const handleChange = (e) => {
    let { name, value, checked, type } = e.target;
    if (type === "checkbox") value = checked;

    if (name === "price") {
      const raw = String(value).replace(/,/g, "");
      if (!isNaN(raw) || raw === "") {
        props.editNewAdvert("displayPrice", formatNumberWithCommas(raw));
        return props.editNewAdvert("price", raw === "" ? "" : Number(raw));
      }
    }

    props.editNewAdvert(name, value);
  };

  const toggleContactPref = (pref) => {
    const cur = Array.isArray(newAdvert.contactPreference) ? newAdvert.contactPreference : [];
    const next = cur.includes(pref) ? cur.filter((p) => p !== pref) : [...cur, pref];
    props.editNewAdvert("contactPreference", next);
  };

  const submit = () => {
    const { phoneNumber, fullname, email } = user;
    if (!fullname || !email || !phoneNumber)
      return toast.error("Lütfen iletişim bilgilerini doldurun");
    if (!isValidTurkishPhoneNumber(phoneNumber))
      return toast.error("Lütfen geçerli bir telefon numarası giriniz");
    if (!newAdvert.title || !newAdvert.body)
      return toast.error("Başlık ve açıklama gerekli");
    if (!newAdvert.price || Number(newAdvert.price) <= 0)
      return toast.error("Geçerli bir fiyat giriniz");
    if (!newAdvert.condition) return toast.error("Ürün durumu seçiniz");
    if (!newAdvert.location.province) return toast.error("Konum seçiniz");
    if (files.length === 0) return toast.error("En az 1 fotoğraf yükleyin");

    const fd = new FormData();
    files.forEach((f) => fd.append("image", f));
    fd.append("kind", "seller");
    fd.append("title", newAdvert.title);
    fd.append("body", newAdvert.body);
    fd.append("price", newAdvert.price);
    fd.append("condition", newAdvert.condition);
    fd.append("tradeable", String(!!newAdvert.tradeable));
    fd.append("contactPreference", JSON.stringify(newAdvert.contactPreference));
    fd.append("category", newAdvert.category);
    fd.append("filters", JSON.stringify(newAdvert.filters));
    fd.append("location", JSON.stringify(newAdvert.location));
    fd.append("selectedCategory", JSON.stringify(newAdvert.selectedCategory));
    fd.append("user", JSON.stringify(user));

    advertEndpoint
      .createAdvertWithoutLogin(fd)
      .then(() => {
        window.scrollTo(0, 0);
        props.clearNewlyCreatedAdvert("seller");
        handleReset();
        setUser({ phoneNumber: "", fullname: "", email: "" });
        setFiles([]);
        setSubmitted(true);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "İlan oluşturulamadı";
        toast.error(msg);
      });
  };

  if (submitted) {
    return (
      <LayoutV1>
        <FastAdvertSuccess kind="seller" />
      </LayoutV1>
    );
  }

  return (
    <LayoutV1>
      <div className="container py-3">
        <div
          className="d-inline-flex align-items-center mb-2"
          style={{
            background: "var(--role-seller-tint, #E6F9F1)",
            color: "var(--role-seller-ink, #0A4A33)",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 99,
              background: "var(--role-seller, #1EC28B)",
              display: "inline-block",
            }}
          />
          Satıcı ilanı
        </div>
        <h3>Ürününü listele</h3>

        <div className="row mt-3">
          <div className="col-md-4 mb-3">
            <TextField
              type="text"
              name="fullname"
              variant="outlined"
              label="İsim"
              onChange={(e) => handleUserFormData("fullname", e.target.value)}
              value={user.fullname}
              style={{ width: "100%", background: "white" }}
            />
          </div>

          <div className="col-md-4 mb-3">
            <TextField
              type="email"
              name="email"
              variant="outlined"
              label="E-Posta"
              onChange={(e) => handleUserFormData("email", e.target.value)}
              value={user.email}
              style={{ width: "100%", background: "white" }}
            />
          </div>

          <div className="col-md-4 mb-4">
            <PhoneField
              value={user.phoneNumber}
              onChange={(e) => handleUserFormData("phoneNumber", e.target.value)}
              style={{ minHeight: 50, height: "100%", borderRadius: 5, borderColor: "#ccc" }}
            />
          </div>
        </div>

        <div className={classes.root}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* 1) Kategori */}
            <Step>
              <StepLabel>Kategori</StepLabel>
              <StepContent>
                <p>Sattığın ürünün kategorisini seç.</p>
                <div className="d-flex flex-row flex-wrap">
                  {categories.map((category) => (
                    <div
                      key={category._id || category.id}
                      onClick={() => selectCategory(category)}
                      className="selectableCategory"
                    >
                      <div style={{ borderColor: "black", borderWidth: 10, fontSize: 30, padding: 5 }}>
                        <img src={category.icon} alt={category.name} width={35} />
                      </div>
                      <div style={{ fontWeight: "bold" }}>{category.name}</div>
                    </div>
                  ))}
                </div>
              </StepContent>
            </Step>

            {/* 2) Fotoğraflar */}
            <Step>
              <StepLabel>Fotoğraflar (1-8)</StepLabel>
              <StepContent>
                <div {...getRootProps()}>
                  <input {...getInputProps()} multiple />
                  <p
                    style={{
                      minHeight: 80,
                      backgroundColor: isDragActive ? "#d9f7e9" : "#eeffeb",
                      borderRadius: 10,
                      padding: 14,
                      color: "black",
                      border: "2px dashed var(--role-seller, #1EC28B)",
                    }}
                  >
                    {isDragActive
                      ? "Şimdi bırak"
                      : "Fotoğrafları buraya sürükle ya da tıkla. İlk fotoğraf kapak olur."}
                  </p>
                </div>

                {files.length > 0 && (
                  <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
                    {files.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          width: 88,
                          height: 88,
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f1f1f1",
                          position: "relative",
                          border: i === 0 ? "2px solid var(--role-seller, #1EC28B)" : "1px solid #e5e5e5",
                        }}
                      >
                        <img
                          src={URL.createObjectURL(f)}
                          alt={`upload-${i}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        {i === 0 && (
                          <div
                            style={{
                              position: "absolute",
                              top: 4,
                              left: 4,
                              background: "#0F1B17",
                              color: "#fff",
                              fontSize: 9,
                              fontWeight: 700,
                              padding: "2px 5px",
                              borderRadius: 4,
                            }}
                          >
                            KAPAK
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disabled={files.length === 0}
                    className={classes.button}
                  >
                    İleri
                  </Button>
                </div>
              </StepContent>
            </Step>

            {/* 3) Title + body */}
            <Step>
              <StepLabel>Başlık & Açıklama</StepLabel>
              <StepContent>
                <div className="d-flex flex-column">
                  <TextField
                    type="text"
                    name="title"
                    variant="outlined"
                    label="İlan Başlığı"
                    onChange={handleChange}
                    value={newAdvert.title}
                    style={{ width: "100%", marginBottom: 12 }}
                  />
                  <TextField
                    type="text"
                    name="body"
                    variant="outlined"
                    label="Açıklama"
                    multiline
                    rows={6}
                    onChange={handleChange}
                    value={newAdvert.body}
                    style={{ width: "100%" }}
                  />
                </div>
                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!newAdvert.title || !newAdvert.body}
                    onClick={handleNext}
                    className={classes.button}
                  >
                    İleri
                  </Button>
                </div>
              </StepContent>
            </Step>

            {/* 4) Price + condition + tradeable */}
            <Step>
              <StepLabel>Fiyat & Durum</StepLabel>
              <StepContent>
                <div className="row">
                  <div className="col-sm-12 col-md-6">
                    <TextField
                      type="text"
                      name="price"
                      variant="outlined"
                      label="Fiyat (₺)"
                      onChange={handleChange}
                      value={newAdvert.displayPrice}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <TextField
                      select
                      name="condition"
                      variant="outlined"
                      label="Ürün durumu"
                      onChange={handleChange}
                      value={newAdvert.condition}
                      style={{ width: "100%" }}
                    >
                      <MenuItem value="new">Sıfır</MenuItem>
                      <MenuItem value="used">2. El</MenuItem>
                      <MenuItem value="refurbished">Yenilenmiş</MenuItem>
                    </TextField>
                  </div>
                </div>
                <div className="d-flex align-items-center mt-2">
                  <span style={{ fontWeight: 600, marginRight: 8 }}>Takas:</span>
                  <Switch
                    name="tradeable"
                    checked={!!newAdvert.tradeable}
                    onChange={handleChange}
                    color="primary"
                  />
                </div>
                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!newAdvert.price || !newAdvert.condition}
                    onClick={handleNext}
                    className={classes.button}
                  >
                    İleri
                  </Button>
                </div>
              </StepContent>
            </Step>

            {/* 5) Location */}
            <Step>
              <StepLabel>Konum</StepLabel>
              <StepContent>
                <ProvinceDistrictSelect />
                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      newAdvert.location.province
                        ? handleNext()
                        : toast.error("Lütfen konum seçin.")
                    }
                  >
                    İleri
                  </Button>
                </div>
              </StepContent>
            </Step>

            {/* 6) Contact pref + filters (optional) */}
            <Step>
              <StepLabel>İletişim</StepLabel>
              <StepContent>
                <p>Alıcılar sana nasıl ulaşsın?</p>
                <div className="d-flex" style={{ gap: 8 }}>
                  {[
                    { value: "phone", label: "Telefon" },
                    { value: "message", label: "Mesaj" },
                  ].map((opt) => {
                    const selected = (newAdvert.contactPreference || []).includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleContactPref(opt.value)}
                        style={{
                          padding: "9px 14px",
                          borderRadius: 999,
                          background: selected ? "var(--role-seller, #1EC28B)" : "#fff",
                          color: selected ? "#fff" : "#3A4541",
                          border: `1.5px solid ${selected ? "var(--role-seller, #1EC28B)" : "#E8EBE9"}`,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {newAdvert.selectedCategory && (
                  <div className="mt-3">
                    <CreateAdvertFilters filters={newAdvert.selectedCategory.filters} />
                  </div>
                )}

                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={(newAdvert.contactPreference || []).length === 0}
                    onClick={handleNext}
                    className={classes.button}
                  >
                    İleri
                  </Button>
                </div>
              </StepContent>
            </Step>

            {/* 7) Publish */}
            <Step>
              <StepLabel>Yayınla</StepLabel>
              <StepContent>
                <p>Yayınla butonu ile ilanın incelenmek üzere sıraya alınır.</p>
                <div className={classes.actionsContainer}>
                  <Button onClick={handleBack} className={classes.button}>Geri</Button>
                  <Button variant="contained" color="primary" onClick={submit}>
                    Yayınla
                  </Button>
                </div>
              </StepContent>
            </Step>
          </Stepper>
        </div>
      </div>
    </LayoutV1>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& .MuiTextField-root": { margin: theme.spacing(1) },
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: { marginBottom: theme.spacing(2) },
}));

export default connect(null, {
  ...advertRedux.actions,
  ...categoryRedux.actions,
})(FastSellerAdvertCreationPage);
